import React from 'react';
import {
    Button, TextField, IconButton, FormControl, InputLabel, Select, MenuItem,
    ListSubheader, FormHelperText, Tooltip
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import {
    DOCUSIGN_ROLE_NAMES,
    DOCUSIGN_INTERNAL_FIELDS,
    DOCUSIGN_FIELD_GROUP_ORDER,
    getInternalField
} from 'constants/docusignTemplates';

export const emptyField = () => ({ tempId: `${Date.now()}-${Math.random()}`, templateName: '', internalName: '' });

export const emptyRole = () => ({
    tempId: `${Date.now()}-${Math.random()}`,
    name: '',
    roleOnDocusignTemplate: '',
    fields: [emptyField()]
});

const DocusignRolesEditor = ({ roles, onChange, errors = {}, disabled }) => {
    const updateRole = (index, updatedRole) => {
        const next = [...roles];
        next[index] = updatedRole;
        onChange(next);
    };

    const addRole = () => onChange([...roles, emptyRole()]);

    const removeRole = (index) => onChange(roles.filter((_, i) => i !== index));

    const addField = (roleIndex) => {
        const role = roles[roleIndex];
        updateRole(roleIndex, { ...role, fields: [...(role.fields || []), emptyField()] });
    };

    const removeField = (roleIndex, fieldIndex) => {
        const role = roles[roleIndex];
        updateRole(roleIndex, { ...role, fields: role.fields.filter((_, i) => i !== fieldIndex) });
    };

    const updateField = (roleIndex, fieldIndex, updatedField) => {
        const role = roles[roleIndex];
        const fields = [...role.fields];
        fields[fieldIndex] = updatedField;
        updateRole(roleIndex, { ...role, fields });
    };

    /**
     * Picking an internal field prefills the tab label with the suggested one. It is only a
     * suggestion: the backend sends the configured templateName as the tabLabel, so it still
     * has to match the label of the tab in the DocuSign template.
     */
    const handleInternalNameChange = (roleIndex, fieldIndex, field, internalName) => {
        const suggested = getInternalField(internalName)?.tabLabel;
        const previousSuggestion = getInternalField(field.internalName)?.tabLabel;
        const keepsCurrentLabel = (field.templateName || '').trim() && field.templateName !== previousSuggestion;
        updateField(roleIndex, fieldIndex, {
            ...field,
            internalName,
            templateName: keepsCurrentLabel ? field.templateName : (suggested || '')
        });
    };

    const usedRoleNames = roles.map((role) => role.name).filter(Boolean);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {typeof errors.roles === 'string' && (
                <div style={{ color: 'var(--status-declined)', fontSize: '0.875rem' }}>{errors.roles}</div>
            )}

            {roles.map((role, roleIndex) => {
                const roleErrors = errors[roleIndex] || {};
                const fieldErrors = roleErrors.fields || {};
                const usedInternalNames = (role.fields || []).map((field) => field.internalName).filter(Boolean);

                return (
                    <div
                        key={role.tempId || role.name || roleIndex}
                        style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '16px' }}
                    >
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                            <FormControl
                                variant="outlined"
                                size="small"
                                style={{ width: '220px' }}
                                error={!!roleErrors.name}
                                disabled={disabled}
                            >
                                <InputLabel id={`role-name-${roleIndex}`}>Role</InputLabel>
                                <Select
                                    labelId={`role-name-${roleIndex}`}
                                    label="Role"
                                    value={role.name || ''}
                                    onChange={(e) => updateRole(roleIndex, { ...role, name: e.target.value })}
                                >
                                    {DOCUSIGN_ROLE_NAMES.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                            disabled={option.value !== role.name && usedRoleNames.includes(option.value)}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                    {/* keeps a value stored by another client visible instead of blanking it out */}
                                    {role.name && !DOCUSIGN_ROLE_NAMES.some((option) => option.value === role.name) && (
                                        <MenuItem value={role.name}>{role.name} (unsupported)</MenuItem>
                                    )}
                                </Select>
                                <FormHelperText>
                                    {roleErrors.name
                                        || DOCUSIGN_ROLE_NAMES.find((option) => option.value === role.name)?.recipient
                                        || 'Determines the envelope recipient'}
                                </FormHelperText>
                            </FormControl>

                            <TextField
                                label="Role on DocuSign template"
                                variant="outlined"
                                size="small"
                                style={{ flex: 1 }}
                                disabled={disabled}
                                value={role.roleOnDocusignTemplate || ''}
                                onChange={(e) => updateRole(roleIndex, { ...role, roleOnDocusignTemplate: e.target.value })}
                                error={!!roleErrors.roleOnDocusignTemplate}
                                helperText={roleErrors.roleOnDocusignTemplate || 'Must match the role name in the DocuSign template exactly (e.g. "Signer 1")'}
                            />

                            <Tooltip title="Remove role">
                                <IconButton
                                    onClick={() => removeRole(roleIndex)}
                                    color="secondary"
                                    size="small"
                                    disabled={disabled}
                                    style={{ marginTop: '4px' }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            <div style={{ color: '#666', fontSize: '0.8125rem', fontWeight: 500, marginBottom: '8px' }}>
                                Fields sent to DocuSign
                            </div>

                            {(role.fields || []).length === 0 && (
                                <div style={{ color: '#999', fontSize: '0.875rem', marginBottom: '8px' }}>
                                    No fields mapped for this role.
                                </div>
                            )}

                            {(role.fields || []).map((field, fieldIndex) => {
                                const currentFieldErrors = fieldErrors[fieldIndex] || {};
                                const internalField = getInternalField(field.internalName);
                                return (
                                    <div
                                        key={field.tempId || `${field.internalName}-${fieldIndex}`}
                                        style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '8px' }}
                                    >
                                        <FormControl
                                            variant="outlined"
                                            size="small"
                                            style={{ flex: 1 }}
                                            error={!!currentFieldErrors.internalName}
                                            disabled={disabled}
                                        >
                                            <InputLabel id={`internal-name-${roleIndex}-${fieldIndex}`}>Internal field</InputLabel>
                                            <Select
                                                labelId={`internal-name-${roleIndex}-${fieldIndex}`}
                                                label="Internal field"
                                                value={field.internalName || ''}
                                                onChange={(e) => handleInternalNameChange(roleIndex, fieldIndex, field, e.target.value)}
                                            >
                                                {DOCUSIGN_FIELD_GROUP_ORDER.map((group) => [
                                                    <ListSubheader key={group}>{group}</ListSubheader>,
                                                    ...DOCUSIGN_INTERNAL_FIELDS
                                                        .filter((option) => option.group === group)
                                                        .map((option) => (
                                                            <MenuItem
                                                                key={option.value}
                                                                value={option.value}
                                                                disabled={option.value !== field.internalName && usedInternalNames.includes(option.value)}
                                                            >
                                                                {option.value}
                                                            </MenuItem>
                                                        ))
                                                ])}
                                            </Select>
                                            <FormHelperText>
                                                {currentFieldErrors.internalName
                                                    || (internalField ? `${internalField.source} · ${internalField.format}` : ' ')}
                                            </FormHelperText>
                                        </FormControl>

                                        <TextField
                                            label="Template field (tab label)"
                                            variant="outlined"
                                            size="small"
                                            style={{ flex: 1 }}
                                            disabled={disabled}
                                            value={field.templateName || ''}
                                            onChange={(e) => updateField(roleIndex, fieldIndex, { ...field, templateName: e.target.value })}
                                            error={!!currentFieldErrors.templateName}
                                            helperText={currentFieldErrors.templateName
                                                || (internalField ? `Suggested: ${internalField.tabLabel}` : 'Must match the tab label in DocuSign exactly')}
                                        />

                                        <Tooltip title="Remove field">
                                            <IconButton
                                                onClick={() => removeField(roleIndex, fieldIndex)}
                                                color="secondary"
                                                size="small"
                                                disabled={disabled}
                                                style={{ marginTop: '4px' }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                );
                            })}

                            <Button
                                startIcon={<AddIcon />}
                                color="primary"
                                size="small"
                                onClick={() => addField(roleIndex)}
                                disabled={disabled}
                            >
                                Add Field
                            </Button>
                        </div>
                    </div>
                );
            })}

            <div>
                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    size="small"
                    variant="outlined"
                    onClick={addRole}
                    disabled={disabled}
                >
                    Add Role
                </Button>
            </div>
        </div>
    );
};

export default DocusignRolesEditor;
